// ============================================================================
// peanutPercs.ts — GOOP ZONE Peanut Percs Database
// Meta-progression stat upgrades purchased between runs with gold
// Lives in: client/src/game/peanutPercs.ts
// ============================================================================

import { rollRng } from './rng'; // BETA 20.92 — seeded balance-roll RNG (reproducible perc magnitudes)

// --- Type Definitions ---

export type PercStat =
  | 'damage'
  | 'attackSpeed'
  | 'maxHp'
  | 'moveSpeed'
  | 'dodgeChance'
  | 'critChance'
  | 'goldBonus'
  | 'knockback'
  | 'projectileSpeed'
  | 'hpRegen'
  | 'shield'
  | 'xpBonus'
  | 'attackSize'
  | 'luck'
  // BETA 15.13 — new stats opened by the new-perk redesign (PSBOT RUN spec)
  | 'lifesteal'           // N01 Sponge Me Daddy
  | 'armor'               // N02 Padded Armor (now properly a separate stat — engine.ts:p.armor)
  | 'thorns'              // N03 Stepmom's Pills
  | 'critDamage'          // N04 Not Blue Chew
  | 'projectileCount'     // N05 Bungulazine (integer)
  | 'overheal'            // N07 Puberty Blockers
  | 'projectilePierce'    // N08 Not Ritalin (integer)
  | 'pickupRange'         // N09 Greedy Tums
  | 'eliteDamage'         // N11 Stompatine
  | 'projectileBounce'    // N12 Mr Silly Pills (integer)
  | 'phaseChance';        // N14 Not Acid

export type UnlockConditionType =
  | 'default'          // Available from the start
  | 'total_kills'      // Kill X enemies across all runs
  | 'total_gold'       // Collect X gold across all runs
  | 'wave_reached'     // Reach wave X in a single run
  | 'total_deaths'     // Die X times
  | 'survive_minutes'  // Survive X minutes in a single run
  | 'level_reached'    // Reach player level X in a single run
  | 'boss_killed';     // Kill a boss

export interface UnlockCondition {
  type: UnlockConditionType;
  /** The threshold value required to unlock (0 for 'default') */
  value: number;
  /** Human-readable description shown in the locked state */
  description: string;
}

/** BETA 13.103 — Conditional perc gate. The perc's bonus only applies when the
 *  condition is met each frame. Evaluated in applyAllBonusesToPlayer. */
export type PercCondition =
  | { type: 'enemies_within'; range: number; minCount: number }     // Bungulazine — swarm
  | { type: 'no_enemies_within'; range: number }                    // Let's Ride XXX — alone
  | { type: 'enemy_subtype'; subtypes: string[] }                   // Stompatine — vs named elites (damage application)
  | { type: 'movement_diagonal' }                                   // Mr Silly Pills — diagonal movement
  | { type: 'recently_damaged'; secondsAfter: number }              // Not Xanax — post-damage burst
  | { type: 'first_n_seconds'; seconds: number }                    // Not Ritalin — early-phase boost
  | { type: 'after_n_seconds'; seconds: number };                   // Not Ritalin — late-phase penalty (paired flag)

/** BETA 13.103 — Scaling perc formula. The perc's bonus is multiplied by a
 *  state-derived value (e.g., missing-HP%, minutes-survived). */
export type PercScaling =
  | { type: 'missing_hp_pct' }            // Not Blue Chew — bonus × (1 - hp/maxHp)
  | { type: 'minutes_survived' }          // Puberty Blockers — bonus × (state.time / 60)
  | { type: 'gold_held_per_100' }         // Greedy Tums — bonus × Math.floor(player.gold / 100)
  | { type: 'gold_spent_per_500' };       // Debt — bonus × Math.floor(_runGoldSpent / 500)

/** BETA 13.103 — Probabilistic perc. Rolls per relevant trigger. */
export type PercProcEffect = 'phase_contact_damage'; // Not Acid — chance to phase enemy contact damage

export interface PeanutPerc {
  id: string;
  name: string;
  description: string;
  flavorText: string;
  /** The player stat this perc modifies */
  stat: PercStat;
  /** Icon URL from CDN */
  iconUrl: string;
  /** Maximum number of ranks purchasable */
  maxLevel: number;
  /** Bonus per rank (percentage or flat depending on isPercent) */
  bonusPerLevel: number;
  /** Whether the bonus is a percentage or flat value */
  isPercent: boolean;
  /** Gold cost of the first rank */
  baseCost: number;
  /** Condition required to make this perc appear in the shop */
  unlockCondition: UnlockCondition;
  /** Accent color for UI rendering (hex) */
  accentColor: string;
  /** Glow color for UI rendering (hex) */
  glowColor: string;

  // ── BETA 13.103 — Schema extension for trade-off / conditional / scaling / probabilistic percs ──

  /** Optional secondary stat that scales DOWN per pick (negative trade-off).
   *  RETIRED BETA 15.13 — the tradeoff schema was stripped; NO active perc
   *  sets secondaryStat, so no perc reduces a stat. (Padded Armor / N02 only
   *  ADDS armor — it does NOT reduce moveSpeed despite the old docs here.)
   *  Field kept for type compatibility + debug panels only. */
  secondaryStat?: PercStat;
  secondaryBonusPerLevel?: number;
  secondaryIsPercent?: boolean;

  /** Optional condition that gates the bonus. If unmet, primary bonus = 0.
   *  Bungulazine swarm bonus, Let's Ride alone bonus, Stompatine vs-named damage,
   *  Silly Pills diagonal MS, Not Xanax post-damage MS, Not Ritalin early-AS-burst. */
  condition?: PercCondition;

  /** Optional secondary condition (paired with condition for split-phase percs
   *  like Not Ritalin: condition fires bonus in early phase, secondaryCondition
   *  fires the secondary stat penalty in late phase). */
  secondaryCondition?: PercCondition;

  /** Optional scaling formula. Bonus value = bonusPerLevel × levels × scale(state). */
  scaling?: PercScaling;

  /** Probabilistic: roll chance per trigger event (per pick — stacks multiplicatively). */
  procChance?: number;
  procEffect?: PercProcEffect;
}

// BETA 22.87 — the old PERC_ICON_URLS block (13 pre-signed manuscdn CDN URLs)
// is GONE: every perc icon now ships locally at /perk-cards/icons/*.webp
// (the Perk Card System v1 art cutouts). Last CDN leak in the perc system.

// --- Peanut Percs Database ---

export const PEANUT_PERCS: PeanutPerc[] = [
  // =====================================================================
  // STARTER PERCS — Unlocked from the start
  // =====================================================================

  {
    // BETA 19.96 — Dylan-approved perc bumps (each pick should feel meaningful in 4-floor + boss game)
    id: 'T01',
    name: 'Heavy Petting',
    description: 'All damage dealt is increased.',
    flavorText: 'Doctor said take one a day. You took the whole bottle.',
    stat: 'damage',
    iconUrl: '/perk-cards/icons/02_heavy_petting.webp',
    maxLevel: 9999,
    // BETA 20.51 — Dylan: per-pick STILL felt weak vs shrines. 2.5 → 4 so the
    // core damage stat lands in ~4 picks. (was 19.96's 2.5)
    bonusPerLevel: 4,
    isPercent: true,
    baseCost: 200,
    unlockCondition: {
      type: 'default',
      value: 0,
      description: 'Available from the start',
    },
    accentColor: '#ff3333',
    glowColor: '#ff000066',
  },

  {
    id: 'T02',
    name: 'Fast Hands',
    description: 'Your weapons fire faster.',
    flavorText: 'Focus. Speed. Results. Side effects include everything.',
    stat: 'attackSpeed',
    iconUrl: '/perk-cards/icons/03_fast_hands.webp',
    maxLevel: 9999,
    bonusPerLevel: 3.0,                           // BETA 20.51 — Dylan: 2.0 → 3.0 (per-pick feel)
    isPercent: true,
    baseCost: 200,
    unlockCondition: {
      type: 'default',
      value: 0,
      description: 'Available from the start',
    },
    accentColor: '#3366ff',
    glowColor: '#0044ff66',
  },

  {
    id: 'T03',
    name: 'Thick Meat',
    description: 'Increases your max HP.',
    flavorText: 'Flintstones shaped. Gummy. Absolutely loaded.',
    stat: 'maxHp',
    iconUrl: '/perk-cards/icons/04_thick_meat.webp',
    maxLevel: 9999,
    // BETA 16.20 — PSBOT cycle-23: 4 → 2. Killa run hit maxHP 529 (5.3×
    // BETA 19.96 — Dylan-approved: 2 → 4. Per-rare pick ~5-6% maxHp bump
    // (visible HP bar gain instead of "did nothing").
    bonusPerLevel: 4,
    isPercent: true,
    baseCost: 200,
    unlockCondition: {
      type: 'default',
      value: 0,
      description: 'Available from the start',
    },
    accentColor: '#33cc33',
    glowColor: '#00cc0066',
  },

  {
    id: 'T04',
    name: 'Quick Finish',
    description: 'You move faster.',
    flavorText: 'It says NOT CRACK on the label. We believe it.',
    stat: 'moveSpeed',
    iconUrl: '/perk-cards/icons/05_quick_finish.webp',
    maxLevel: 9999,
    bonusPerLevel: 3,                             // BETA 20.51 — Dylan: 1.5 → 3 (per-pick feel)
    isPercent: true,
    baseCost: 200,
    unlockCondition: {
      type: 'default',
      value: 0,
      description: 'Available from the start',
    },
    accentColor: '#ffcc00',
    glowColor: '#ccaa0066',
  },

  // =====================================================================
  // UNLOCKABLE PERCS — Require gameplay milestones
  // =====================================================================

  {
    id: 'T05',
    name: 'Slippery When Wet',
    description: 'Chance to dodge incoming hits.',
    flavorText: 'Your body moves before your brain does. That is concerning.',
    stat: 'dodgeChance',
    iconUrl: '/perk-cards/icons/06_slippery_when_wet.webp',
    maxLevel: 9999,
    bonusPerLevel: 2.5,                           // BETA 19.96 — Dylan-approved: 1.2 → 2.5. Per-rare ~3.5% dodge. Cap 80%.
    isPercent: true,
    baseCost: 600,
    unlockCondition: {
      type: 'total_kills',
      value: 500,
      description: 'Kill 500 enemies total',
    },
    accentColor: '#00cccc',
    glowColor: '#00aaaa66',
  },

  {
    id: 'T06',
    name: 'Lucky Stroke',
    description: 'Chance to land critical hits.',
    flavorText: 'You can see the weak spots. All of them. Even on furniture.',
    stat: 'critChance',
    iconUrl: '/perk-cards/icons/07_lucky_stroke.webp',
    maxLevel: 9999,
    bonusPerLevel: 3.0,                           // BETA 20.51 — Dylan: 2.0 → 3.0 (per-pick feel)
    isPercent: true,
    baseCost: 600,
    unlockCondition: {
      type: 'total_kills',
      value: 1000,
      description: 'Kill 1,000 enemies total',
    },
    accentColor: '#9933cc',
    glowColor: '#7700aa66',
  },

  {
    id: 'T07',
    name: 'Gold Digger',
    description: 'Earn more credits from everything.',
    flavorText: 'Gold coins taste like pennies. You eat them anyway.',
    stat: 'goldBonus',
    iconUrl: '/perk-cards/icons/09_gold_digger.webp',
    maxLevel: 9999,
    // BETA 20.51 — Dylan: 1.5 → 3 (per-pick feel). cycle-23 cut it to 1.5 for a
    // Killa gold-outlier; per-pick feel wins now.
    bonusPerLevel: 3,
    isPercent: true,
    baseCost: 300,
    unlockCondition: {
      type: 'total_gold',
      value: 5000,
      description: 'Collect 5,000 gold total',
    },
    accentColor: '#cc9900',
    glowColor: '#aa880066',
  },

  {
    // BETA 19.96 — Dylan-approved bump (already big, modest 4→6)
    id: 'T08',
    name: 'Back Shots',
    description: 'Your hits knock enemies back harder.',
    flavorText: 'Enemies fly further. Do not ask why.',
    stat: 'knockback',
    iconUrl: '/perk-cards/icons/15_back_shots.webp',
    maxLevel: 9999,
    bonusPerLevel: 6,                             // BETA 19.96 — Dylan-approved: 4 → 6. Per-rare ~8.4% knockback.
    isPercent: true,
    baseCost: 600,
    unlockCondition: {
      type: 'wave_reached',
      value: 10,
      description: 'Reach wave 10 in a single run',
    },
    accentColor: '#8b5e3c',
    glowColor: '#6b3e1c66',
  },

  {
    id: 'T09',
    name: 'Premature Fire',
    description: 'Your projectiles travel faster.',
    flavorText: 'Triple shot espresso. Quadruple shot projectiles.',
    stat: 'projectileSpeed',
    iconUrl: '/perk-cards/icons/20_premature_fire.webp',
    maxLevel: 9999,
    bonusPerLevel: 3,
    isPercent: true,
    baseCost: 600,
    unlockCondition: {
      type: 'wave_reached',
      value: 15,
      description: 'Reach wave 15 in a single run',
    },
    accentColor: '#ff6600',
    glowColor: '#cc440066',
  },

  {
    id: 'T10',
    name: 'Morning Wood',
    description: 'Regenerate HP every second.',
    flavorText: 'For headaches, body aches, and getting shot repeatedly.',
    stat: 'hpRegen',
    iconUrl: '/perk-cards/icons/21_morning_wood.webp',
    maxLevel: 9999,
    bonusPerLevel: 3,                             // BETA 20.51 — Dylan: 1.5 → 3 (per-pick feel)
    isPercent: false,
    baseCost: 300,
    unlockCondition: {
      type: 'total_deaths',
      value: 10,
      description: 'Die 10 times',
    },
    accentColor: '#ff66aa',
    glowColor: '#cc448866',
  },

  {
    id: 'T11',
    name: 'Protection',
    description: 'A protective shield that blocks damage.',
    flavorText: 'Heartburn? No. Bullet-burn? Also no.',
    stat: 'shield',
    iconUrl: '/perk-cards/icons/01_protection.webp',
    maxLevel: 9999,
    bonusPerLevel: 8,                             // BETA 20.51 — Dylan: 3 → 8 ("had to pick 15 shields to feel it")
    isPercent: false,
    baseCost: 900,
    unlockCondition: {
      type: 'survive_minutes',
      value: 15,
      description: 'Survive 15 minutes in a single run',
    },
    accentColor: '#00aacc',
    glowColor: '#008aaa66',
  },

  {
    id: 'T12',
    name: 'Fast Learner',
    description: 'Gain more XP from kills.',
    flavorText: 'You want more. You always want more. That is the problem.',
    stat: 'xpBonus',
    iconUrl: '/perk-cards/icons/10_fast_learner.webp',
    maxLevel: 9999,
    bonusPerLevel: 4,                             // BETA 20.51 — Dylan: 2 → 4 (per-pick feel)
    isPercent: true,
    baseCost: 900,
    unlockCondition: {
      type: 'level_reached',
      value: 20,
      description: 'Reach level 20 in a single run',
    },
    accentColor: '#aaaaaa',
    glowColor: '#88888866',
  },

  {
    id: 'T13',
    name: 'Girth',
    description: 'Bigger attacks. Bigger area.',
    flavorText: 'Everything gets bigger. EVERYTHING.',
    stat: 'attackSize',
    iconUrl: '/perk-cards/icons/24_girth.webp',
    maxLevel: 9999,
    bonusPerLevel: 2.5,
    isPercent: true,
    baseCost: 1000,
    unlockCondition: {
      type: 'boss_killed',
      value: 1,
      description: 'Kill a boss',
    },
    accentColor: '#2244aa',
    glowColor: '#112288666',
  },

  {
    id: 'T14',
    name: 'Happy Ending',
    // BETA 16.27 — PSBOT cycle-30 C.1: bonusPerLevel 2 → 3. Per cycle-30
    // pacing audit (168 real runs): median final luck = 0%, only 5% of
    // runs reach 50%+ luck. Luck system unused at scale. Boosting Lady
    // Luck per-pick magnitude is the primary lever for "luck reachable
    // in median runs" target (15-30%). Combined with Mr Silly passive
    // bump and Luck shrine bump.
    description: 'Better luck. Better rolls.',
    flavorText: 'She winked at you. You don\'t know why. Don\'t question it.',
    stat: 'luck',
    iconUrl: '/perk-cards/icons/18_happy_ending.webp',
    maxLevel: 9999,
    bonusPerLevel: 5,                             // BETA 20.51 — Dylan: 3 → 5 (per-pick feel)
    isPercent: false,
    baseCost: 700,
    // BETA 19.13 — Was "Kill 500 enemies across all runs", identical to
    // T05 Reflexes ("Kill 500 enemies total"). Quest screen showed both
    // gated on the same milestone. Bumped to 1500 so the unlock progression
    // is distinct + Lady Luck (the luck-stat perk) is gated behind a more
    // meaningful kill count than the basic dodge perk.
    unlockCondition: {
      type: 'total_kills',
      value: 1500,
      description: 'Kill 1,500 enemies total',
    },
    accentColor: '#ffdd00',
    glowColor: '#ccaa0066',
  },

  // ═══════════════════════════════════════════════════════════════
  //  BETA 13.103 — Peanut Percs Pack 2 (14 NEW PERCS)
  //  Four new categories: trade-off, conditional, scaling, probabilistic.
  //  All per-pick scaling for consistency with T01-T14 pattern.
  // ═══════════════════════════════════════════════════════════════

  // ─────────────────────────────────────────────────────────────────────────
  // BETA 15.13 — NEW PERK REDESIGN per PSBOT RUN spec ("simplified — flat
  // buffs only"). Each new perk does ONE thing. Stack cap = 40 (was 5).
  // ALL conditional / scaling / procEffect / secondaryStat / dual-phase
  // logic STRIPPED. The original 14 T-perks (start-menu photos) are
  // UNCHANGED above.
  //
  // Cross-check vs T01-T14 — every new perk anchors on a DIFFERENT stat:
  //   N01 lifesteal · N02 armor · N03 thorns · N04 critDamage
  //   N05 projectileCount · N06 (mechanic — bungulator summon)
  //   N07 overheal · N08 projectilePierce · N09 pickupRange
  //   N10 (mechanic — gold-revive) · N11 eliteDamage
  //   N12 projectileBounce · N13 (mechanic — slow-on-hit-taken)
  //   N14 phaseChance
  //
  // 11 STAT-ANCHOR perks shipped here. 3 MECHANIC perks (N06/N10/N13)
  // keep their `stat` anchor for UI but their REAL effect is implemented
  // in BETA 15.14. Marked with `[MECHANIC PERK]` comments.
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'N01',
    name: 'Suck Tax',
    description: 'Heal a cut of the damage you deal.',
    flavorText: 'Sponge first. Ask questions never.',
    stat: 'lifesteal',
    iconUrl: '/perk-cards/icons/13_suck_tax.webp',
    maxLevel: 9999,
    bonusPerLevel: 2,                             // BETA 20.51 — Dylan: 0.75 → 2 (lifesteal felt like nothing per pick)
    isPercent: true,
    baseCost: 600,
    unlockCondition: { type: 'wave_reached', value: 10, description: 'Reach wave 10 in a single run' },
    accentColor: '#ff5577',
    glowColor: '#ff336666',
  },
  {
    id: 'N02',
    name: 'Safe Word',
    description: 'Take reduced damage from all hits.',
    flavorText: "Heavy. Ugly. Effective.",
    stat: 'armor',
    iconUrl: '/perk-cards/icons/12_safe_word.webp',
    maxLevel: 9999,
    bonusPerLevel: 4,              // BETA 20.51 — Dylan: 2 → 4 (per-pick feel)
    isPercent: false,
    baseCost: 600,
    unlockCondition: { type: 'total_deaths', value: 5, description: 'Die 5 times across all runs' },
    accentColor: '#888899',
    glowColor: '#55556666',
  },
  {
    id: 'N03',
    name: 'Touch Me Not',
    description: 'Reflect damage back when hit.',
    flavorText: "She left them on the counter. They have a purpose.",
    stat: 'thorns',
    iconUrl: '/perk-cards/icons/14_touch_me_not.webp',
    maxLevel: 9999,
    bonusPerLevel: 2.5,            // BETA 19.97 — Dylan-approved: 1 → 2.5. Per-rare ~3.5% (was ~1.4% = invisible). thorns shrine uncommon = +15% one-shot for reference.
    isPercent: true,
    baseCost: 1000,
    unlockCondition: { type: 'default', value: 0, description: 'Available from the start' },
    accentColor: '#ff77ff',
    glowColor: '#ff44ff66',
  },
  {
    id: 'N04',
    name: 'Deep Impact',
    description: 'Critical hits deal more damage.',
    flavorText: 'Gets harder the closer you get to dying.',
    stat: 'critDamage',
    iconUrl: '/perk-cards/icons/08_deep_impact.webp',
    maxLevel: 9999,
    bonusPerLevel: 3,              // BETA 19.96 — Dylan-approved: 1.5 → 3. Per-rare ~4.2% crit dmg.
    isPercent: true,
    baseCost: 700,
    unlockCondition: { type: 'wave_reached', value: 10, description: 'Reach wave 10 in a single run' },
    accentColor: '#3366ff',
    glowColor: '#0033aa66',
  },
  // BETA 17.88 — N05 Bungulazine DELETED. Dylan: "the bungulator projectile
  // perc may be too over powered that no one will play the game without
  // chosing that perc. that should be probably some kind of buncha bullshii
  // item and also have something to do with bungulator combo ness."
  // → Repeatable +1 proj/lvl perc removed entirely. Replaced by chest-only
  // item `item_bungula_chambers` (in bunchaBullshiiii.ts) with sqrt scaling
  // (floor(sqrt(stacks))) and a 1.5× damage synergy when paired with the
  // Bungulators minion item. This matches VS's Duplicator pattern: single
  // build-defining source, gated, with diminishing-return curve baked in.
  //
  // BETA 17.88 — N06 Let's Ride XXX DELETED. Per-pick effect was
  // bonusPerLevel: 0 since BETA 15.13 — the perc was a literal no-op
  // (comment: "0 until 15.14 wires the spawn mechanic", never shipped).
  // 9+ builds in the LUDICROUS 50-sweep wasted picks on this. Removing
  // until the spawn mechanic actually exists.

  {
    id: 'N07',
    name: 'Overstuffed',
    description: 'Healing can push HP past your max.',
    flavorText: 'Slow growth. Strong roots.',
    stat: 'overheal',
    iconUrl: '/perk-cards/icons/26_overstuffed.webp',
    maxLevel: 9999,
    bonusPerLevel: 5,                             // BETA 20.51 — Dylan: 3 → 5 (buffer should feel meaningful per pick; was cut to 3 in 15.47)
    isPercent: false,
    baseCost: 800,
    unlockCondition: { type: 'survive_minutes', value: 8, description: 'Survive 8 minutes in a single run' },
    accentColor: '#cc99ff',
    glowColor: '#9966cc66',
  },
  {
    id: 'N08',
    name: 'Just The Tip',
    description: 'Shots pierce through extra enemies.',
    flavorText: 'Crash and burn. Mostly burn.',
    stat: 'projectilePierce',
    iconUrl: '/perk-cards/icons/16_just_the_tip.webp',
    maxLevel: 9999,
    bonusPerLevel: 1.5,            // BETA 19.96 — Dylan-approved: 0.5 → 1.5. Per-rare ~2.1 pierce.
    isPercent: false,
    baseCost: 700,
    unlockCondition: { type: 'survive_minutes', value: 5, description: 'Survive 5 minutes in a single run' },
    accentColor: '#ffaa33',
    glowColor: '#ff770066',
  },
  {
    id: 'N09',
    name: 'Reach Around',
    description: 'Pick up loot from further away.',
    flavorText: 'Donate. Donate. Donate.',
    stat: 'pickupRange',
    iconUrl: '/perk-cards/icons/11_reach_around.webp',
    maxLevel: 9999,
    bonusPerLevel: 8,             // BETA 21.39 — 2.5 → 8 so each pick is clearly felt (5 picks = +40% radius).
    isPercent: true,
    baseCost: 600,
    unlockCondition: { type: 'default', value: 0, description: 'Available from the start' },
    accentColor: '#ffd700',
    glowColor: '#cca80066',
  },
  {
    id: 'N10',
    // BETA 22.90 — was LAST PUMP / Debt ("gold absorbs the hit"). Dylan:
    // "change the name and what it does. delete the old gold stuff." Gold died
    // with the token migration; the Debt mechanic is deleted everywhere. New
    // mechanic lives in engine.ts: per-frame proximity check → radial
    // knockback shockwave on cooldown (HEART_THROB_* constants).
    name: 'Heart Throb',
    // BETA 22.91 — trigger changed per Dylan: fires on LOW HP (the red-flicker
    // state), not proximity.
    description: 'When your health drops critical, release a knockback shockwave.',
    flavorText: 'Ba-bump. Back up.',
    stat: 'maxHp',                 // [MECHANIC PERK] — anchor for UI only. Real effect is the Heart Throb shockwave in engine.ts.
    iconUrl: '/perk-cards/icons/23_last_pump.webp',
    // One pick = activated for the rest of the run (Dylan's 20.00 passive
    // pattern: own it = active, frees the rolls for everything else).
    maxLevel: 1,
    bonusPerLevel: 0,              // mechanic is binary: own it = active
    isPercent: false,
    baseCost: 700,
    unlockCondition: { type: 'default', value: 0, description: 'Available from the start' },
    accentColor: '#ff4d7d',
    glowColor: '#ff4d7d55',
  },
  {
    id: 'N11',
    name: 'Daddy Issues',
    // BETA 19.98 — Description was "to bosses" but the apply path
    // (weaponAdapter.ts elite-damage block) only fired on `variant === 'elite'`,
    // excluding `variant === 'boss'` + mega bosses. Now Stompatine ALSO buffs
    // damage against bosses + mega (the spec). Description matches the code.
    description: 'Deal bonus damage to elites and bosses.',
    flavorText: 'Boss-killer juice.',
    stat: 'eliteDamage',
    iconUrl: '/perk-cards/icons/19_daddy_issues.webp',
    maxLevel: 9999,
    bonusPerLevel: 3,              // BETA 19.96 — Dylan-approved: 1.5 → 3. Per-rare ~4.2% elite dmg.
    isPercent: true,
    baseCost: 700,
    // BETA 19.13 — Was "Kill a boss" (value 1), identical to T13 Not Viagra.
    // Quest screen showed both gated on the same milestone. Bumped to 3 so
    // the unlock progression is distinct + Stompatine (the boss-damage perk)
    // is gated behind multiple boss kills, which fits its identity.
    unlockCondition: { type: 'boss_killed', value: 3, description: 'Kill 3 bosses' },
    accentColor: '#996644',
    glowColor: '#66442266',
  },
  {
    id: 'N12',
    name: 'Double Tap',
    description: 'Shots bounce to extra enemies.',
    flavorText: 'Off-axis is the only axis.',
    stat: 'projectileBounce',
    iconUrl: '/perk-cards/icons/17_double_tap.webp',
    maxLevel: 9999,
    bonusPerLevel: 1.5,            // BETA 19.96 — Dylan-approved: 0.5 → 1.5. Per-rare ~2.1 bounces.
    isPercent: false,
    baseCost: 600,
    unlockCondition: { type: 'default', value: 0, description: 'Available from the start' },
    accentColor: '#ffee44',
    glowColor: '#ccaa1166',
  },
  {
    id: 'N13',
    name: 'Not Xanax',
    description: 'Enemies who hit you are slowed to 50% speed.',
    flavorText: 'Calm down by running away.',
    stat: 'knockback',             // [MECHANIC PERK] — anchor for UI only. Real effect (slow-on-hit-taken) wired in BETA 15.15 below. The bonusPerLevel feeds the slow-duration formula, NOT player.knockbackBonus. Engine reads slot.accumulatedBonus directly in enemySystem.ts contact-damage block.
    iconUrl: '/items/perc_not_xanax.png',
    maxLevel: 9999,
    bonusPerLevel: 1.0,            // BETA 15.15 — per-pick rolls 0.5–1.5 (with rarity+jitter), accumulated at 40 picks ~30–60. Slow duration formula: min(3.0s, 1.0 + 0.05 × accumulated). At accumulated 40 → cap reached (3s). At accumulated 10 → 1.5s slow. accumulatedBonus drives strength; "save count" not relevant here (every contact hit triggers the slow).
    isPercent: false,
    baseCost: 600,
    unlockCondition: { type: 'wave_reached', value: 6, description: 'Reach wave 6 in a single run' },
    accentColor: '#aaffaa',
    glowColor: '#66cc6666',
  },
  {
    id: 'N14',
    name: 'No Contact',
    description: 'Chance to ignore contact damage entirely.',
    flavorText: "Trip the room. Take no damage. Or do, idk man, it's a trip.",
    stat: 'phaseChance',
    iconUrl: '/perk-cards/icons/22_no_contact.webp',
    maxLevel: 9999,
    bonusPerLevel: 1.5,            // BETA 19.96 — Dylan-approved: 0.5 → 1.5. Per-rare ~2.1% phase.
    isPercent: true,
    baseCost: 700,
    unlockCondition: { type: 'wave_reached', value: 12, description: 'Reach wave 12 in a single run' },
    accentColor: '#ff66ff',
    glowColor: '#cc33cc66',
  },

  // ══════════════════════════════════════════════════════════════════════
  // N15 — MONEY SHOT (BETA 22.87, Perk Card System v1). Adopts the orphaned
  // projectileCount stat (dead since N05 Bungulazine was deleted 17.88 — the
  // soft-cap + apply path in peanutPercSystem still consume it). Integer
  // family: card bonus comes from CARD_INT_STEPS in perkCards.ts (+1, +2 at
  // legendary), never the fractional multiplier path.
  // ══════════════════════════════════════════════════════════════════════
  {
    id: 'N15',
    name: 'Money Shot',
    description: 'Fire +1 projectile with every shot.',
    flavorText: 'Make every load count.',
    stat: 'projectileCount',
    iconUrl: '/perk-cards/icons/25_money_shot.webp',
    maxLevel: 4,
    bonusPerLevel: 1,
    isPercent: false,
    baseCost: 900,
    unlockCondition: { type: 'default', value: 0, description: 'Available from the start' },
    accentColor: '#ffd700',
    glowColor: 'rgba(255,215,0,0.5)',
  },
];

// --- Helper Functions ---

/** Get all Peanut Percs */
export function getAllPercs(): PeanutPerc[] {
  return PEANUT_PERCS;
}

/** Get a Peanut Perc by ID */
export function getPercById(id: string): PeanutPerc | undefined {
  return PEANUT_PERCS.find(p => p.id === id);
}

/** Get all percs that are unlocked by default */
export function getStarterPercs(): PeanutPerc[] {
  return PEANUT_PERCS.filter(p => p.unlockCondition.type === 'default');
}

/** Get all percs that require unlocking */
export function getLockablePercs(): PeanutPerc[] {
  return PEANUT_PERCS.filter(p => p.unlockCondition.type !== 'default');
}

/** Calculate the cost of the next rank for a specific perc */
export function calculatePercCost(perc: PeanutPerc, currentLevel: number, totalRanksBought: number): number {
  if (currentLevel >= perc.maxLevel) return Infinity;

  // BaseCost = InitialPrice × (1 + currentLevel)
  const baseCost = perc.baseCost * (1 + currentLevel);

  // Fees scale with total ranks bought across all percs
  // Fee = floor(20 × 1.1^totalRanksBought) if totalRanksBought >= 1, else 0
  const fees = totalRanksBought >= 1
    ? Math.floor(20 * Math.pow(1.1, totalRanksBought))
    : 0;

  return baseCost + fees;
}

/** Calculate the total stat bonus for a perc at a given level */
export function calculatePercBonus(perc: PeanutPerc, level: number): number {
  return perc.bonusPerLevel * Math.min(level, perc.maxLevel);
}

/** Calculate the maximum possible bonus for a perc */
export function calculatePercMaxBonus(perc: PeanutPerc): number {
  return perc.bonusPerLevel * perc.maxLevel;
}

/** Get a formatted string describing the total bonus at max level */
export function getPercMaxDescription(perc: PeanutPerc): string {
  const maxBonus = calculatePercMaxBonus(perc);
  const suffix = perc.isPercent ? '%' : '';
  return `+${maxBonus}${suffix} ${perc.stat}`;
}

// --- Compatibility Shims (for peanutPercSystem.ts v3) ---
// The in-run system uses UpgradeRarity for level-up rarity rolls.
// These bridge the meta-progression data format with the in-run system.

export type UpgradeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

/** Rarity multipliers for in-run upgrade bonus calculation.
 *  BETA 19.2 — Softened from 1.0/1.3/1.6/2.0/3.0 to Megabonk's
 *  1.0/1.2/1.4/1.6/2.0 curve. The old 3× legendary + ±50% jitter could
 *  produce a 4.5× spread between common and legendary, which was the
 *  primary "1 FPS snowball" engine. New cap is 2× × 1.5 jitter max = 3×
 *  spread. Jitter system retained (Goopzone signature). */
const RARITY_MULTIPLIERS: Record<UpgradeRarity, number> = {
  // BETA 20.21 — 3-tier (Dylan): common / rare / legendary only. rollUpgradeRarity
  // no longer returns uncommon/epic; their entries mirror the tier they collapse
  // into. Bigger gaps (1.0 / 1.5 / 2.5) so rarity is clearly readable and — with
  // the tightened jitter below — a rare ALWAYS beats a common of the same perc.
  common: 1.0,
  uncommon: 1.0,   // collapsed → common
  rare: 1.5,
  epic: 1.5,       // collapsed → rare
  legendary: 2.5,
};

/**
 * Calculate the bonus amount for a single upgrade pick at a given rarity.
 * Used by peanutPercSystem when generating level-up options.
 *
 * BETA 15.7 — bumped jitter ±10% → ±50% per Dylan: "there should be 1000
 * random variations. not just a couple of damage buf 1.2% or 2.4%. and
 * that is it. there needs to be so many. so that when you play you feel
 * like you are always getting something new."
 *
 * Math:
 * - Rarity multiplier table: common 1.0×, uncommon 1.3×, rare 1.6×,
 *   epic 2.0×, legendary 3.0×.
 * - Jitter range [0.50, 1.50] applied as a uniform random factor on top.
 * - Bonus = bonusPerLevel × rarityMul × jitter
 * - With ~200 distinct jitter buckets (each ~0.005 step over the 1.0 range)
 *   and 5 rarities, that's ~1000 distinct effective bonus values per perc.
 *
 * The jitter is computed at OFFER time and locked in when the player picks.
 * Tracked in `state.slots[i].accumulatedBonus` (existing wiring) so the
 * exact rolled value is recorded per pick — visible in CSV under
 * `percLevels` + `percTracker.currentEffectiveBonus`.
 */
// BETA 20.77 — THE shared luck→magnitude multiplier (Dylan: "the numbers on
// the buffs need to be higher depending on my luck"). Used by level-up perc
// rolls (below) AND shrine buff values (itemsAdapter). One formula so luck
// feels consistent everywhere: ×1.0 at luck 0, ×1.5 at 200, ×2.0 at 400+.
export function luckValueMult(luck: number): number {
  return 1 + Math.min(Math.max(0, luck), 400) / 400;
}

// BETA 20.97 — THE shared per-pick JITTER factor, extracted from the perc
// system so shrines / chests / weapon cards all "fluctuate" with the SAME
// approved math instead of being fixed integers (Dylan: "the buff numbers...
// are not fixed either? that feels clunky. that is why we have luck"). Returns
// a multiplier centred near 1.0 within a per-rarity window; high luck biases it
// toward the upper end (see calculateUpgradeBonus comments). Seeded via
// rollRng() so the seeded-runs system stays reproducible. Callers multiply this
// into their base value and round (1 decimal for continuous stats, whole for
// integer stats like projectile count). The 'negative' shrine rarity is not an
// UpgradeRarity — callers must skip it before calling this.
export function rollBuffJitter(rarity: UpgradeRarity, playerLuck: number = 0): number {
  const JITTER_BY_RARITY: Record<UpgradeRarity, [number, number]> = {
    common:    [0.85, 1.15],
    uncommon:  [0.85, 1.15],
    rare:      [0.90, 1.15],
    epic:      [0.90, 1.15],
    legendary: [0.90, 1.15],
  };
  const [jMin, jMax] = JITTER_BY_RARITY[rarity] ?? [0.85, 1.15];
  const luckMod = Math.min(Math.max(playerLuck, 0) / 200, 0.5);
  const jitterMinAdjusted = jMin + luckMod * (jMax - jMin);
  return jitterMinAdjusted + rollRng() * (jMax - jitterMinAdjusted); // BETA 20.92 — seeded
}

export function calculateUpgradeBonus(perc: PeanutPerc, rarity: UpgradeRarity, playerLuck: number = 0): number {
  // BETA 15.48 — Per PSBOT cycle-17 upgrade-system audit: common/uncommon
  // jitter was 196% wide. Tightened jitter per rarity:
  //   common:    ±20%   (0.80-1.20)
  //   uncommon:  ±20%   (0.80-1.20)
  //   rare:      ±40%   (0.60-1.40)
  //   epic:      ±50%   (0.50-1.50)
  //   legendary: ±50%   (0.50-1.50)
  // BETA 20.21 — tightened so tiers NEVER overlap (the old ±20% common vs ±20%
  // uncommon let a lucky common beat an unlucky uncommon — Dylan's complaint).
  // With multipliers 1.0/1.5/2.5: common max 1.0×1.15=1.15 < rare min 1.5×0.9=1.35;
  // rare max 1.65 < legendary min 2.25. Same perc → rarity strictly orders the buff.
  // BETA 16.27 — PSBOT cycle-30 C.3: LUCK-WEIGHTED JITTER BIAS. Per Dylan:
  // luck should bias magnitude toward upper end of jitter range. Combined
  // with C.2 rarity curve, high-luck builds get MORE legendaries AND
  // bigger numbers within them (Dylan's "high luck = whole different
  // game" intent). BETA 20.97 — the jitter math now lives in the shared
  // rollBuffJitter() above so shrines/chests/weapon cards fluctuate the same way.
  const jitter = rollBuffJitter(rarity, playerLuck);

  // BETA 20.77 — LUCK SCALES THE NUMBER ITSELF (Dylan: "the numbers on the
  // buffs need to be higher depending on my luck. same with the level up
  // buffs"). The jitter-floor trim above maxed out at +6% average — vestigial.
  // luckValueMult is a GLOBAL multiplier so the 20.21 rarity-ordering
  // guarantee (common < rare < legendary for the same perc) is preserved.
  // ×1.0 at luck 0 → ×1.5 at 200 → ×2.0 at 400+ (hard ceiling).
  const raw = perc.bonusPerLevel * RARITY_MULTIPLIERS[rarity] * jitter * luckValueMult(playerLuck);
  // BETA 16.25 — PSBOT cycle-26 P0.1 CRITICAL FIX. Per audit: T-percs were
  // applying at 100× too small (T01 1.0%/pick → 0.01%/pick actual).
  //
  // Root cause: `calculateUpgradeBonus` was dividing isPercent percs by
  // 100 to convert to a "fraction" (0.015 for 1.5%). But the apply path
  // — `applyAllBonusesToPlayer` (`p.damageBonus += bonuses.damage`) and
  // `applyPercBonusesToStats` (`baseDamage * (1 + bonuses.damage / 100)`)
  // — both expect INTEGER PERCENT form (1.5 for 1.5%) per the BETA 14.46
  // fix that established this convention. So the /100 here produced a
  // 100× under-application across the entire perc system. WeaponLab and
  // itemsAdapter manual-level-up paths set `slot.accumulatedBonus =
  // perc.bonusPerLevel` directly (already integer percent — they were
  // working). Real-game level-up was the broken path.
  //
  // Fix: return integer percent always. `formatPercBonus` updated to
  // match (no longer multiplies by 100 for isPercent).
  //
  // Effect on existing data: previous live_play rows have delta values
  // 100× too small for isPercent percs. PSBOT analyses that summed
  // pickEvent deltas to verify final stat application can now compare
  // delta directly to attackSpeedBonus increment (sums should match).
  return parseFloat(raw.toFixed(4));
}

/**
 * Format a perc bonus for display.
 * Used by peanutPercSystem for level-up option display strings.
 */
export function formatPercBonus(perc: PeanutPerc, totalBonus: number): string {
  // BETA 16.25 — PSBOT cycle-26 P0.1 paired fix. Was multiplying by 100
  // because calculateUpgradeBonus returned fraction (0.015 for 1.5%);
  // since the apply path needs integer percent and we fixed the bonus
  // generator to return integer percent, the formatter must NOT multiply
  // by 100 anymore. Direct render: 1.5 → "+1.5%".
  if (perc.isPercent) {
    return `+${totalBonus.toFixed(1)}%`;
  }
  return `+${totalBonus.toFixed(1)}`;
}
